import { cn } from "@/lib/utils";
import type { NormalizedShop } from "@mm-sales/shared";
import { Check, Minus, X } from "lucide-react";

/**
 * What an import actually brought in, and what it did not: business facts,
 * brand, hours, and the menu's coverage (photos, descriptions, options,
 * prices). Rendered under every finished import and on the review step so the
 * rep knows what the demo will be missing before building it.
 */
export function shopReport(shop: NormalizedShop) {
  const b = shop.business;
  const items = shop.items;
  const n = items.length;
  const withImage = items.filter((i) => i.imageUrl).length;
  const withDesc = items.filter((i) => i.description?.trim()).length;
  const withMods = items.filter((i) => i.modifiers.length).length;
  const withVariants = items.filter((i) => i.variants.length).length;
  const noPrice = items.filter((i) => !(i.price > 0)).length;
  const daysOpen = shop.hours.filter((h) => !h.closed).length;
  const addr = b.address;
  const facts: { label: string; ok: boolean; value?: string }[] = [
    { label: "Business name", ok: Boolean(b.name), value: b.name },
    { label: "Phone", ok: Boolean(b.phone), value: b.phone },
    { label: "Email", ok: Boolean(b.email), value: b.email },
    {
      label: "Address",
      ok: Boolean(addr.line1 && addr.city),
      value: [addr.line1, addr.city, addr.state, addr.postalCode].filter(Boolean).join(", "),
    },
    { label: "Website", ok: Boolean(b.website), value: b.website },
    { label: "Cuisine / type", ok: Boolean(b.cuisine), value: b.cuisine },
    { label: "Description", ok: Boolean(b.description?.trim()) },
    {
      label: "Hours",
      ok: shop.hours.length > 0,
      value: shop.hours.length ? `${daysOpen} day${daysOpen === 1 ? "" : "s"} open` : undefined,
    },
    {
      label: "Rating",
      ok: b.rating != null,
      value:
        b.rating != null
          ? `${b.rating}${b.reviewCount ? ` (${b.reviewCount} reviews)` : ""}`
          : undefined,
    },
  ];
  const brand: { label: string; ok: boolean; value?: string }[] = [
    { label: "Logo", ok: Boolean(b.logoUrl) },
    { label: "Cover photo", ok: Boolean(b.heroImageUrl) },
    { label: "Brand colour", ok: Boolean(b.brandColor), value: b.brandColor },
    { label: "Second colour", ok: Boolean(b.brandSecondaryColor), value: b.brandSecondaryColor },
    { label: "Heading font", ok: Boolean(b.headingFont), value: b.headingFont },
    { label: "Body font", ok: Boolean(b.bodyFont), value: b.bodyFont },
    { label: "Favicon", ok: Boolean(b.faviconUrl) },
    { label: "Gallery photos", ok: shop.images.length > 0, value: String(shop.images.length) },
  ];
  const menu = {
    items: n,
    categories: shop.categories.length,
    withImage,
    withoutImage: n - withImage,
    withDesc,
    withoutDesc: n - withDesc,
    withMods,
    withVariants,
    noPrice,
  };
  const missing = [...facts, ...brand].filter((f) => !f.ok).map((f) => f.label);
  return { facts, brand, menu, missing };
}

function Row({ label, ok, value }: { label: string; ok: boolean; value?: string }) {
  return (
    <li className="flex items-center gap-2 py-0.5 text-xs">
      {ok ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-success" />
      ) : (
        <X className="h-3.5 w-3.5 shrink-0 text-destructive" />
      )}
      <span className={cn(ok ? "" : "text-muted-foreground")}>{label}</span>
      {ok && value ? (
        <span className="ml-auto max-w-[55%] truncate text-muted-foreground" title={value}>
          {value}
        </span>
      ) : !ok ? (
        <span className="ml-auto text-muted-foreground">not imported</span>
      ) : null}
    </li>
  );
}

function Stat({ n, label, warn }: { n: number; label: string; warn?: boolean }) {
  return (
    <div
      className={cn("rounded-md p-2 text-center", warn && n > 0 ? "bg-destructive/10" : "bg-muted")}
    >
      <div className={cn("text-lg font-semibold", warn && n > 0 ? "text-destructive" : "")}>
        {n}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

export function ImportReport({
  shop,
  compact = false,
}: { shop: NormalizedShop; compact?: boolean }) {
  const r = shopReport(shop);
  return (
    <div className="space-y-3">
      <div className={cn("grid gap-2", compact ? "grid-cols-3" : "grid-cols-3 md:grid-cols-9")}>
        <Stat n={r.menu.items} label="items" />
        <Stat n={r.menu.categories} label="categories" />
        <Stat n={r.menu.withImage} label="with photo" />
        <Stat n={r.menu.withoutImage} label="no photo" warn />
        <Stat n={r.menu.withDesc} label="with description" />
        <Stat n={r.menu.withoutDesc} label="no description" warn />
        <Stat n={r.menu.withMods} label="with options" />
        <Stat n={r.menu.withVariants} label="with sizes" />
        <Stat n={r.menu.noPrice} label="no price" warn />
      </div>
      {!compact ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Business
            </div>
            <ul className="divide-y">
              {r.facts.map((f) => (
                <Row key={f.label} {...f} />
              ))}
            </ul>
          </div>
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Brand & photos
            </div>
            <ul className="divide-y">
              {r.brand.map((f) => (
                <Row key={f.label} {...f} />
              ))}
            </ul>
          </div>
        </div>
      ) : r.missing.length ? (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Minus className="mt-0.5 h-3 w-3 shrink-0" />
          Not imported: {r.missing.join(", ")}.
        </p>
      ) : null}
    </div>
  );
}
